<template>
  <div class="catalog-category">
    <DelayHydration v-if="pageData">
      <div class="l-container header-title-wrap has-breadcrumbs">
        <h1 class="text-h1-bold-up">
          {{ pageData.name }}
        </h1>
      </div>
      <div class="l-container l-description-grid category-names-wrap">
        <ul class="category-name-list category-name-list">
          <li
            v-for="(category, index) in categoryNameList"
            :key="String(index)"
          >
            <CatalogCategoryName :category="category" />
          </li>
        </ul>
      </div>
      <ul class="l-container l-description-grid categories">
        <CatalogCategoryItem
          v-for="item in pageData.children"
          :key="item.id"
          :item="item"
          :loaded-top-presets="loadedTopPresets"
          :loaded-brand-presets="loadedBrandPresets"
          :loaded-top-models="loadedTopModels"
          :page-data="pageData"
        />
      </ul>
      <div class="l-container l-description-grid category-names-wrap">
        <ModelHistory
          font-class="heading-l"
          class="model-similar--full heading-l"
          :no-margin="false"
          :no-padding="true"
        />
      </div>
    </DelayHydration>
    <client-only>
      <LazyModalsAdultModal
        v-if="adultOpenAdultModal"
        @confirm="onAdultConfirm"
      />
    </client-only>
  </div>
</template>
<script setup lang="ts">
import { useGlobalStore } from '~/stores/global';
import { API } from '~/api/api';
import type {
  loadedBrandPresetsType,
  LoadedTopModelsType,
  LoadedTopPresetsType,
  ResponseCategory,
} from '~/interfaces/api';
import { base64ToObj, objToBase64 } from '~/utils/base64';

const props = defineProps({
  data: {
    type: Object as any,
    required: true,
  },
  seoData: {
    type: Object as any,
    required: true,
  },
  pageData: {
    type: Object as any,
    required: true,
  },
});
const emit = defineEmits(['change-data']);
const globalStore = useGlobalStore();
const loadedBrandPresets = ref<loadedBrandPresetsType>({});
const loadedTopPresets = ref<LoadedTopPresetsType>({});
const loadedTopModels = ref<LoadedTopModelsType>({});
const adultOpenAdultModal = computed(() => globalStore.isOpenAdultModal);
const category_id = computed(() => props.data?.category_id || 0);
const adult = computed(() => globalStore.adult);
const isBot = computed(() => globalStore.isBot);
const getCatalogData = async () => {
  const loadItems: ResponseCategory[] = props.pageData?.children || [null];
  try {
    const itemsData = await Promise.all(
      loadItems.map(async (item) => {
        const result = { brandPresets: {}, topPresets: {}, models: {} };
        if (item) {
          if (!item.has_children) {
            const blocksResponse = await API.get(
              `/categories/${item.id}/blocks?region_id=${globalStore.region.id}&block=top_presets&block=brand_presets`
            );
            const blocksData = blocksResponse.data;
            if (blocksData) {
              if (blocksData.blocks && blocksData.blocks.length > 1) {
                result.brandPresets = {
                  [item.id || 0]: blocksData.blocks[1].items,
                };
                result.topPresets = {
                  [item.id || 0]: blocksData.blocks[0].items,
                };
              }
            }
          }
          const modelsArrRes = await API.post(
            `/categories/${item.id}/list`,
            null,
            {
              params: {
                region_id: globalStore.region.id,
                page: 1,
                per_page: 10,
                adult: adult.value,
                sem: globalStore.sem || undefined,
                device: globalStore.device || 1,
              },
            }
          );
          const modelsArr = modelsArrRes.data;
          if (modelsArr && modelsArr.list && modelsArr.list.length) {
            result.models = { [item.id || 0]: modelsArr.list };
          }
          return result;
        }
      })
    );
    const blocks = itemsData.reduce(
      (data, item) => {
        return {
          brandPresets: {
            ...(data?.brandPresets || {}),
            ...(item?.brandPresets || {}),
          },
          topPresets: {
            ...(data?.topPresets || {}),
            ...(item?.topPresets || {}),
          },
          models: { ...(data?.models || {}), ...(item?.models || {}) },
        };
      },
      { brandPresets: {}, topPresets: {}, models: {} }
    );
    return blocks;
  } catch (e) {
    // console.error('fetch error', e);
    return null;
  }
};
const asyncData = await useAsyncData(
  `asyncCatalogCategoryData_${category_id.value}`,
  async () => {
    const data = await getCatalogData();
    return objToBase64({ data });
  }
);
if (asyncData.data?.value) {
  const data = base64ToObj(asyncData.data?.value).data;
  if (data) {
    loadedBrandPresets.value = data?.brandPresets || {};
    loadedTopPresets.value = data?.topPresets || {};
    loadedTopModels.value = data?.models || {};
  }
}
const categoryNameList = computed(() =>
  props.pageData?.children?.length ? props.pageData?.children : []
);

function onAdultConfirm() {
  emit('change-data', {
    key: 'products',
    data: {},
  });
}

onMounted(() => {
  const isAdultChildren = props.pageData?.children.find(
    (item: ResponseCategory) => item?.adult
  );
  if (isAdultChildren?.adult && !isBot.value) {
    globalStore.setOpenAdultModal(true);
  }
});
watch(
  () => adult.value,
  async () => {
    if (!adultOpenAdultModal.value || isBot.value) return;
    const res = await getCatalogData();
    if (res) {
      loadedBrandPresets.value = res?.brandPresets;
      loadedTopPresets.value = res?.topPresets;
      loadedTopModels.value = res?.models;
    }
  }
);
useHead(() => {
  return {
    title: `${props.seoData?.page?.title}`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `${props.seoData?.page?.description}`,
      },
      {
        hid: 'keywords',
        name: 'keywords',
        content: `${props.seoData?.page?.keywords}`,
      },
      {
        property: 'og:title',
        content: `${props.seoData?.page?.title}`,
      },
      {
        property: 'og:description',
        content: `${props.seoData?.page?.description}`,
      },
    ],
  };
});
</script>
<style scoped lang="scss">
.catalog-category {
  min-height: 100vh;

  .category-names-wrap {
    margin-bottom: 40px;

    @include md-block {
      margin-bottom: 48px;
    }

    @include xxl-block {
      margin-bottom: 65px;
    }
  }

  .category-name-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: auto;
    grid-column-gap: 8px;
    grid-row-gap: 8px;
    @include sm-block {
      grid-template-columns: repeat(4, 1fr);
    }
    @include md-block {
      grid-column-gap: 12px;
      grid-row-gap: 12px;
    }
    @include lg-block {
      grid-template-columns: repeat(6, 1fr);
    }
    @include xl-block {
      grid-column-gap: 16px;
      grid-row-gap: 16px;
      grid-template-columns: repeat(6, 1fr);
    }
  }

  .category-name-list-3 {
    display: grid;
    grid-template-columns: 100%;
    grid-auto-rows: auto;
    border-radius: 8px;
    grid-column-gap: 8px;
    grid-row-gap: 8px;
    margin: 0;
    @include sm-block {
      grid-template-columns: repeat(2, 1fr);
    }
    @include md-block {
      grid-template-columns: repeat(3, 1fr);
    }
    @include lg-block {
      grid-template-columns: repeat(4, 1fr);
    }
    @include xl-block {
      grid-template-columns: repeat(5, 1fr);
      grid-column-gap: 16px;
      grid-row-gap: 16px;
    }
  }
}
</style>
